import { useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, CircularProgress,
  Collapse, IconButton, Tooltip, Dialog, DialogContent,
  DialogActions, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockIcon from '@mui/icons-material/Lock';
import FlagIcon from '@mui/icons-material/Flag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import type { Arbeitspaket, KanbanBoard } from '@pm/shared';
import { useApKanbanBoards, useCreateKanbanBoard, useRenameKanbanBoard, unwrapBoards, normalizeKanbanState } from '../../../../api/hooks/useKanban';
import { useUpdateArbeitspaket } from '../../../../api/hooks/useArbeitspakete';
import { AP_DONE_STATUS } from '../../lib/apStatus';
import { useNotifications } from '../../../../context/NotificationContext';
import { KanbanBoardView } from './KanbanBoardView';
import { apiErrorMessage } from '../../../../lib/apiErrorMessage';
import { AppDialogTitle } from '../../../../components/common/AppDialogTitle';

/**
 * PHQ-89: Alle (sichtbaren, nicht abgebrochenen) Aufgaben des Boards erledigt?
 * Ohne Aufgaben ist das Board nicht „fertig" — es gäbe nichts abzuschließen.
 */
export function allBoardCardsDone(cards: ReadonlyArray<Pick<KanbanBoard['cards'][number], 'state' | 'hidden'>>): boolean {
  const relevant = cards.filter((c) => !c.hidden && c.state !== 'CANCELED');
  return relevant.length > 0 && relevant.every((c) => normalizeKanbanState(c.state) === 'COMPLETED');
}

interface Props {
  projectId: string;
  ap: Arbeitspaket;
  showHidden: boolean;
  /** Personen-Picker-Optionen (P-14): RACI-Personen aller APs des Projekts. */
  personOptions?: string[];
}

export function ApKanbanSection({ projectId, ap, showHidden, personOptions = [] }: Props) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { data, isLoading } = useApKanbanBoards(projectId, ap.id);
  const createBoard = useCreateKanbanBoard(projectId);
  const renameBoard = useRenameKanbanBoard(projectId);
  const updateAp = useUpdateArbeitspaket(projectId);
  const [openFrozen, setOpenFrozen] = useState<Record<string, boolean>>({});
  const [stichwort, setStichwort] = useState<string | null>(null); // !==null => Dialog offen

  const boards = unwrapBoards(data); // seq DESC: [0] = aktiv
  const active: KanbanBoard | undefined = boards.find((b) => !b.frozen) ?? boards[0];
  const frozen = boards.filter((b) => b.id !== active?.id);
  const busy = createBoard.isPending || renameBoard.isPending || updateAp.isPending;
  const apDone = ap.status === AP_DONE_STATUS;
  const canClose = !apDone && !!active && !active.frozen && allBoardCardsDone(active.cards);

  // „Board schließen" (Ziellinie): AP wird auf Erledigt gesetzt, Fortschritt 100 % —
  // der Gantt färbt den Balken daraufhin grün (PHQ-87).
  const handleCloseBoard = async () => {
    try {
      await updateAp.mutateAsync({ id: ap.id, data: { status: AP_DONE_STATUS, progress: 100 } });
      notify(t('kanban.boardClosed', 'Board geschlossen — Arbeitspaket ist erledigt'), 'success');
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  // Erstes Board (nichts einzufrieren): direkt anlegen. Sonst: Stichwort für das
  // einzufrierende (aktive) Board erfragen, vorbelegt mit dessen aktuellem (KW-)Label.
  const handleNewBoard = () => {
    if (!active) { void createFirstOrConfirmed(); return; }
    setStichwort(active.label);
  };

  const createFirstOrConfirmed = async () => {
    try {
      // Aktives Board vor dem Einfrieren auf das gewählte Stichwort umbenennen.
      if (active && stichwort !== null && stichwort.trim() && stichwort.trim() !== active.label) {
        await renameBoard.mutateAsync({ apId: ap.id, boardId: active.id, label: stichwort.trim() });
      }
      await createBoard.mutateAsync(ap.id);
      setStichwort(null);
      notify(t('kanban.boardCreated', 'Neues Board angelegt'), 'success');
    } catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip label={`AP ${ap.nr}`} size="small" color="primary" variant="outlined" />
          <Typography variant="subtitle1" fontWeight={600}>{ap.titel || t('kanban.untitledAp', 'Ohne Titel')}</Typography>
          {ap.raci_r && (
            <Chip label={`R: ${ap.raci_r}`} size="small" variant="outlined" />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {apDone && (
            <Chip icon={<CheckCircleIcon />} label={t('kanban.apDone', 'AP erledigt')} size="small" color="success" />
          )}
          {canClose && (
            <Button size="small" variant="contained" color="success" startIcon={<FlagIcon />} onClick={() => void handleCloseBoard()} disabled={busy}>
              {t('kanban.closeBoard', 'Board schließen')}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleNewBoard} disabled={busy}>
            {t('kanban.newBoard', 'Neues Board')}
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
      ) : boards.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          {t('kanban.noBoards', 'Noch kein Board. „Neues Board" legt das erste an (mit R-Karte, falls R gesetzt).')}
        </Typography>
      ) : (
        <>
          {active && (
            <Box sx={{ mb: frozen.length ? 2 : 0 }}>
              <Typography variant="caption" sx={{ color: 'var(--pm-fg-accent)', fontWeight: 700, display: 'block', mb: 0.5 }}>
                {active.label} · {t('kanban.activeBoard', 'aktiv')}
              </Typography>
              <KanbanBoardView projectId={projectId} apId={ap.id} board={active} active={!active.frozen} showHidden={showHidden} personOptions={personOptions} />
            </Box>
          )}

          {frozen.map((board) => {
            const isOpen = !!openFrozen[board.id];
            return (
              <Box key={board.id} sx={{ borderTop: '1px dashed var(--pm-border)', pt: 1, mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title={isOpen ? tc('actions.collapse', 'Einklappen') : tc('actions.expand', 'Ausklappen')}>
                    <IconButton size="small" onClick={() => setOpenFrozen((p) => ({ ...p, [board.id]: !isOpen }))}>
                      {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <LockIcon sx={{ fontSize: 15, color: 'var(--pm-fg-subtle)' }} />
                  <Typography variant="caption" color="text.secondary">
                    {board.label} · {t('kanban.frozen', 'eingefroren')} · {board.cards.length} {t('kanban.cards', 'Karten')}
                  </Typography>
                </Stack>
                <Collapse in={isOpen} unmountOnExit>
                  <Box sx={{ mt: 1, opacity: 0.85 }}>
                    <KanbanBoardView projectId={projectId} apId={ap.id} board={board} active={false} showHidden={showHidden} personOptions={personOptions} />
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </>
      )}

      <Dialog open={stichwort !== null} onClose={() => setStichwort(null)} maxWidth="xs" fullWidth>
        <AppDialogTitle>{t('kanban.freezeTitle', 'Aktuelles Board einfrieren')}</AppDialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('kanban.freezeHint', 'Unter welchem Stichwort soll das bisherige Board gespeichert werden? Anschließend wird ein neues, leeres Board mit den übernommenen Karten angelegt.')}
          </Typography>
          <TextField
            autoFocus fullWidth
            label={t('kanban.freezeKeyword', 'Stichwort')}
            value={stichwort ?? ''}
            onChange={(e) => setStichwort(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && stichwort?.trim()) void createFirstOrConfirmed(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStichwort(null)}>{tc('actions.cancel', 'Abbrechen')}</Button>
          <Button variant="contained" disabled={!stichwort?.trim() || busy} onClick={() => void createFirstOrConfirmed()}>
            {t('kanban.newBoard', 'Neues Board')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
